/**
 * Pickup detail (workstream C) — the supervisor's view of one warehouse run: the
 * window + ordered stops, who's on it (assign here), the status, and the orders
 * it covers with each project's packing checklist rolled up to "picked up". Edit
 * opens the shared PickupForm; Delete removes the run (and its crew blocks).
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronLeft,
  Circle,
  ClipboardList,
  Loader2,
  MapPin,
  Pencil,
  Trash2,
  Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import type { PickupProjectSummary, PickupStatus } from '@vari-crew/schemas';
import { useDeletePickup, usePickup, useUpdatePickup } from '@/api/pickups';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog, Dialog } from '@/components/ui/Dialog';
import { PickupCrewCard } from '@/components/pickups/PickupCrewCard';
import { PickupForm } from '@/components/pickups/PickupForm';
import { pickupAddressLine } from '@/components/projects/PickupLocationsEditor';
import { cn } from '@/lib/cn';

const STATUS_STYLE: Record<string, string> = {
  scheduled: 'bg-fill text-ink-dim',
  in_progress: 'bg-status-onsite text-status-onsite-fg',
  complete: 'bg-status-complete text-status-complete-fg',
  cancelled: 'bg-fill text-ink-faint',
};
const STATUS_OPTIONS: { value: PickupStatus; label: string }[] = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
];

function fmtWindow(start: string | null, end: string | null): string {
  if (!start) return 'Not scheduled yet';
  const s = new Date(start);
  const d = s.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const t = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return end ? `${d} · ${t(start)}–${t(end)}` : `${d} · ${t(start)}`;
}

export function PickupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const canManage = me?.role === 'supervisor' || me?.role === 'admin';
  const { data: pickup, isLoading, error } = usePickup(id);
  const update = useUpdatePickup(id ?? '');
  const del = useDeletePickup(id ?? '');
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !pickup) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to="/pickups" className="text-sm text-ink-dim hover:text-ink inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Pickups
        </Link>
        <Card className="mt-4">
          <CardContent className="py-8 text-center text-sm text-status-blocked-fg">
            {(error as Error | undefined)?.message ?? 'This pickup could not be found.'}
          </CardContent>
        </Card>
      </div>
    );
  }

  const readOnlyReason = pickup.status === 'complete' || pickup.status === 'cancelled' ? pickup.status : null;

  function setStatus(status: PickupStatus) {
    update.mutate({ status }, { onError: (e) => toast.error(`Could not update: ${e.message}`) });
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <Link to="/pickups" className="text-sm text-ink-dim hover:text-ink inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Pickups
      </Link>

      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold flex items-center gap-2 min-w-0">
            <Truck className="w-6 h-6 text-ink-faint shrink-0" />
            <span className="truncate">{pickup.display_title}</span>
          </h1>
          <div className="text-sm text-ink-dim mt-0.5">{fmtWindow(pickup.scheduled_start, pickup.scheduled_end)}</div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <select
              value={pickup.status}
              onChange={(e) => setStatus(e.target.value as PickupStatus)}
              disabled={update.isPending}
              className={cn('h-8 rounded-full border-0 px-3 text-[11px] font-medium', STATUS_STYLE[pickup.status])}
              aria-label="Pickup status"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </header>

      {/* Stops / route */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-ink-faint" /> Stops
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pickup.stops.length === 0 ? (
            <div className="text-sm text-ink-dim">No stops yet.{canManage && ' Add them via Edit.'}</div>
          ) : (
            <ol className="space-y-2">
              {pickup.stops.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-fill text-ink-dim grid place-items-center text-[11px] font-medium">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{s.name}</div>
                    <div className="text-[11px] text-ink-faint truncate">{pickupAddressLine(s)}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <PickupCrewCard
        pickup={{ id: pickup.id, title: pickup.display_title, scheduled_start: pickup.scheduled_start }}
        canManage={canManage}
        readOnlyReason={readOnlyReason}
      />

      {/* Orders + their packing checklists */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-ink-faint" /> Orders ({pickup.projects.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pickup.projects.length === 0 ? (
            <div className="text-sm text-ink-dim">No orders linked.</div>
          ) : (
            pickup.projects.map((p) => <OrderChecklist key={p.project_id} project={p} />)
          )}
        </CardContent>
      </Card>

      {pickup.notes && (
        <Card>
          <CardContent className="py-3 text-sm whitespace-pre-wrap text-ink-dim">{pickup.notes}</CardContent>
        </Card>
      )}

      {editing && (
        <Dialog open onClose={() => setEditing(false)} busy={update.isPending} title={<>Edit pickup</>} className="max-w-lg">
          <PickupForm
            submitLabel="Save"
            pending={update.isPending}
            onCancel={() => setEditing(false)}
            initial={{
              title: pickup.title,
              project_ids: pickup.projects.map((p) => p.project_id),
              stops: pickup.stops,
              scheduled_start: pickup.scheduled_start,
              scheduled_end: pickup.scheduled_end,
              notes: pickup.notes,
            }}
            onSubmit={(v) =>
              update.mutate(v, {
                onSuccess: () => {
                  toast.success('Pickup updated');
                  setEditing(false);
                },
                onError: (e) => toast.error(`Could not save: ${e.message}`),
              })
            }
          />
        </Dialog>
      )}

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        busy={del.isPending}
        title="Delete pickup?"
        body="This removes the run and unassigns its crew. To keep it on record instead, set the status to Cancelled."
        confirmLabel="Delete"
        tone="danger"
        onConfirm={() =>
          del.mutate(undefined, {
            onSuccess: () => {
              toast.success('Pickup deleted');
              navigate('/pickups');
            },
            onError: (e) => toast.error(`Could not delete: ${e.message}`),
          })
        }
      />
    </div>
  );
}

/** One linked project's packing list, rolled up to "picked up". */
function OrderChecklist({ project }: { project: PickupProjectSummary }) {
  const { packing } = project;
  return (
    <div className="rounded-lg border border-line p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{project.title}</div>
          {project.client_name && <div className="text-[11px] text-ink-faint truncate">{project.client_name}</div>}
        </div>
        {project.picked_up ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-status-complete-fg font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" /> Picked up
          </span>
        ) : (
          <span className="shrink-0 text-[11px] text-ink-faint">
            {packing.received}/{packing.total} received
            {packing.backordered > 0 && ` · ${packing.backordered} back-ordered`}
          </span>
        )}
      </div>
      {project.line_items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {project.line_items.map((it) => (
            <li key={it.id} className="flex items-center gap-2 text-[13px]">
              {it.received ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-status-complete-fg shrink-0" />
              ) : (
                <Circle className="w-3.5 h-3.5 text-ink-faint shrink-0" />
              )}
              <span className={cn('min-w-0 flex-1 truncate', it.received && 'text-ink-faint line-through')}>
                {it.product_code && <span className="text-ink-faint">{it.product_code} · </span>}
                {it.description}
              </span>
              {it.is_backordered && (
                <span className="shrink-0 text-[10px] text-amber-700">
                  back-ordered {it.shipped ?? 0}/{it.quantity ?? 0}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
